import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import { ArticleSchema, BreadcrumbListSchema, WebPageSchema } from '@/components/base/SEO';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const relatedArticles = articles.filter((a) => a.slug !== slug).slice(0, 3);

  const pageTitle = article
    ? `${article.title} | 51网址导航 吃瓜爆料`
    : '文章未找到 | 51网址导航';
  const pageDesc = article?.excerpt || '51网址导航吃瓜爆料，每日更新最新网红黑料、明星塌房现场及独家爆料视频。';
  const canonicalUrl = `http://chiguadao.cn/article/${slug}`;

  useEffect(() => {
    document.title = pageTitle;
    document.querySelector('meta[name="description"]')?.setAttribute('content', pageDesc);
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', canonicalUrl);

    const existingScript = document.getElementById('schema-jsonld');
    if (existingScript) existingScript.remove();

    if (article) {
      const schemas = [
        WebPageSchema(pageTitle, canonicalUrl, pageDesc),
        BreadcrumbListSchema([
          { name: '首页', url: 'http://chiguadao.cn/' },
          { name: '吃瓜爆料', url: 'http://chiguadao.cn/article/chigua-2026-0706' },
          { name: article.title, url: canonicalUrl },
        ]),
        ArticleSchema({
          title: article.title,
          url: canonicalUrl,
          description: article.excerpt,
          image: article.coverImage,
          datePublished: article.publishDate,
          dateModified: article.publishDate,
          authorName: article.author,
        }),
      ];

      const jsonLd = JSON.stringify({ '@context': 'https://schema.org', '@graph': schemas }, null, 2);
      const script = document.createElement('script');
      script.id = 'schema-jsonld';
      script.type = 'application/ld+json';
      script.textContent = jsonLd;
      document.head.appendChild(script);
    }
  }, [slug, pageTitle, pageDesc, canonicalUrl, article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading font-bold text-2xl text-foreground-900 mb-3">文章未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您查找的文章不存在或已被删除。</p>
            <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="pt-20 min-h-screen">
        <article>
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: '吃瓜爆料', path: '/article/chigua-2026-0706' },
                { name: article.title },
              ]}
            />

            <div className="py-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-label font-semibold">
                  {article.category}
                </span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-label">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-900 leading-snug mb-6">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500 mb-8">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {article.readCount} 阅读
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-chat-3-line"></i>
                  {article.commentCount.toLocaleString()} 评论
                </span>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 mb-10">
            <div className="relative aspect-[16/9] rounded-2xl overflow-hidden mb-10">
              <img
                src={article.coverImage}
                alt={article.title}
                title={`${article.title} - 51网址导航`}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="prose prose-lg max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200 flex flex-wrap items-center gap-3">
              <span className="text-sm text-foreground-500 font-label font-medium">标签：</span>
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1.5 rounded-full bg-secondary-50 text-secondary-700 text-xs font-label cursor-pointer hover:bg-secondary-100 transition-colors">
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-8 flex items-center justify-between">
              <Link
                to="/"
                className="flex items-center gap-2 text-foreground-500 hover:text-primary-500 text-sm font-label transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回首页
              </Link>
              <div className="flex items-center gap-4">
                <button className="w-9 h-9 rounded-full border border-foreground-200 flex items-center justify-center text-foreground-500 hover:border-primary-300 hover:text-primary-500 transition-colors cursor-pointer" aria-label="分享到微博">
                  <i className="ri-weibo-line"></i>
                </button>
                <button className="w-9 h-9 rounded-full border border-foreground-200 flex items-center justify-center text-foreground-500 hover:border-primary-300 hover:text-primary-500 transition-colors cursor-pointer" aria-label="分享到微信">
                  <i className="ri-wechat-line"></i>
                </button>
                <button className="w-9 h-9 rounded-full border border-foreground-200 flex items-center justify-center text-foreground-500 hover:border-primary-300 hover:text-primary-500 transition-colors cursor-pointer" aria-label="复制链接">
                  <i className="ri-link"></i>
                </button>
              </div>
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <div className="bg-background-100 py-14">
              <div className="max-w-4xl mx-auto px-4 md:px-6">
                <h2 className="font-heading font-bold text-xl text-foreground-900 mb-8">相关爆料</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.slug}`}
                      className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
                    >
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img
                          src={ra.coverImage}
                          alt={ra.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="font-heading font-bold text-sm text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors">
                          {ra.title}
                        </h3>
                        <p className="mt-2 text-xs text-foreground-500">{ra.publishDate}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}